import { useEffect, useRef, useState } from 'react';
import { api, errorText } from '../api';
import { Dialog } from '../components/Dialog';
import { eurC, parseEuro } from '../lib/money';
import { isBilltoro } from '../produkt';
import { ChipRadios } from './ChipRadios';
import { deQty, parseQty } from './formular';
import { lagerUrl, type LagerListe } from './lager/LagerDaten';
import { PdfDialog } from './PdfDialog';
import type { KorrekturPruefung, KorrekturStand, Position, RtItem } from './types';

const BILLTORO = isBilltoro();
// Billtoro, Rechnung mit Artikeln, die Bestand führen: die Korrektur bucht nie (lager.md 3.8); ein Satz je Herkunft
const LAGER_AUS_LS =
  'Den Bestand hat der Lieferschein gebucht. Kommt Ware zurück, buchen Sie sie unter Lager als Zugang. Stornieren Sie nach einer vollen Korrektur den Lieferschein, legt das Storno die Menge zurück; dann bitte keinen Zugang buchen.';
const LAGER_SONST =
  'Kommt Ware zurück, buchen Sie sie unter Lager als Zugang. Für eine neue Rechnung über dieselbe Lieferung ist Stornieren der richtige Weg (so zählt die Lieferung nur einmal).';

// „Gutschrift / Korrektur“: einer geschriebenen Rechnung einen Teil gutschreiben (auch wenn sie bezahlt ist). Es
// entsteht eine Rechnungskorrektur mit eigener Nummer (server korrektur.ts); die Rechnung selbst bleibt, wie sie ist.
// Je Position eine Menge oder ein Betrag (Nachlass), dazu freie Beträge oder „Alles Übrige“. Beträge netto wie bei den
// Positionen; die Zusammenfassung rechnet der Server (nach jeder Änderung, kurz entprellt).

export interface KorrekturErgebnis {
  id: string;
  number: string;
  amount: number;
  offen: number;
  erstattung: number;
  voll: boolean;
}

// je Position: Menge oder Betrag netto (leer = nichts)
type PosWahl = { betrag: boolean; wert: string };
type Frei = { key: number; text: string; betrag: string; steuer: string };
const MAX_FREI = 20;
let nextKey = 1;

// Betrag mit Minus in der Währung der Rechnung
const minus = (v: number, w: string) => (v ? `−${eurC(Math.abs(v), w)}` : eurC(0, w));
const url = (id: string, was = '') => `/api/rechnungstool/rechnung/${id}/korrektur${was}`;

export function KorrekturDialog({
  item,
  firmId,
  onClose,
  onDone,
}: {
  item: RtItem | null;
  firmId: string;
  onClose(): void;
  onDone(r: KorrekturErgebnis): Promise<void>;
}) {
  // Beträge in der Währung der Rechnung
  const wa = item?.waehrung ?? 'EUR';
  const [stand, setStand] = useState<KorrekturStand | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [pos, setPos] = useState<Record<number, PosWahl>>({});
  const [frei, setFrei] = useState<Frei[]>([]);
  const [alles, setAlles] = useState(false);
  const [grund, setGrund] = useState('');
  // Zusammenfassung mit der Anfrage, für die sie gerechnet ist
  const [pruefung, setPruefung] = useState<{ json: string; p: KorrekturPruefung } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [vorschau, setVorschau] = useState<string | null>(null);
  const lauf = useRef(0);
  const [lagerSatz, setLagerSatz] = useState<string | null>(null);
  useEffect(() => () => void (vorschau && URL.revokeObjectURL(vorschau)), [vorschau]);

  // Billtoro: Positionen der Rechnung mit einem Artikel aus dem Lager? Dann der Satz nach Herkunft der Rechnung (aus
  // Lieferscheinen oder nicht). Ohne Antwort kein Satz (nur ein Hinweis).
  const ausLs = !!item?.vorgang?.aus.some((x) => x.art === 'lieferschein');
  useEffect(() => {
    setLagerSatz(null);
    if (!BILLTORO || !item) return;
    let weg = false;
    Promise.all([api.get<{ items: Position[] }>(`/api/rechnungstool/rechnung/${item.id}/vorlage`), api.get<LagerListe>(lagerUrl(firmId))])
      .then(([v, l]) => {
        const imLager = new Set(l.artikel.map((x) => x.id));
        if (!weg && v.items.some((i) => i.artikelId && imLager.has(i.artikelId))) setLagerSatz(ausLs ? LAGER_AUS_LS : LAGER_SONST);
      })
      .catch(() => undefined);
    return () => {
      weg = true;
    };
    // nur beim Öffnen für eine Rechnung
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, firmId]);

  // beim Öffnen für eine Rechnung: Stand laden, Eingaben leeren
  const id = item?.id ?? null;
  useEffect(() => {
    setStand(null);
    setLadeFehler(null);
    setPos({});
    setFrei([]);
    setAlles(false);
    setGrund('');
    setPruefung(null);
    setFehler(null);
    if (!id) return;
    let weg = false;
    api
      .get<KorrekturStand>(url(id))
      .then((s) => !weg && setStand(s))
      .catch((err) => !weg && setLadeFehler(errorText(err)));
    return () => {
      weg = true;
    };
  }, [id]);

  const ku = stand?.kleinunternehmer ?? false;
  const steuernMitRest = (stand?.steuern ?? []).filter((s) => s.nochNetto > 0);
  const rest = stand ? Math.round((stand.betrag - stand.gutgeschrieben) * 100) / 100 : 0;

  // Eingaben → Anfrage an den Server (null: nichts gewählt); Fehler beim Lesen der Zahlen gleich hier
  function anfrage(): { body: object | null; fehler: string | null } {
    if (alles) return { body: { alles: true, grund: grund.trim() || null }, fehler: null };
    const positionen: { pos: number; menge?: number; betrag?: number }[] = [];
    for (const p of stand?.positionen ?? []) {
      const w = pos[p.pos];
      if (!w || !w.wert.trim()) continue;
      if (w.betrag) {
        const b = parseEuro(w.wert);
        if (typeof b !== 'number' || b <= 0) return { body: null, fehler: `Position ${p.pos}: Bitte den Betrag prüfen, z. B. 50,00.` };
        positionen.push({ pos: p.pos, betrag: b });
      } else {
        const m = parseQty(w.wert);
        if (m === undefined) return { body: null, fehler: `Position ${p.pos}: Bitte die Menge prüfen, z. B. 1 oder 2,5.` };
        positionen.push({ pos: p.pos, menge: m });
      }
    }
    const freie: { text: string; betrag: number; steuer: string }[] = [];
    for (const f of frei) {
      if (!f.text.trim() && !f.betrag.trim()) continue;
      const b = parseEuro(f.betrag);
      if (typeof b !== 'number' || b <= 0) return { body: null, fehler: 'Freier Betrag: Bitte den Betrag prüfen, z. B. 50,00.' };
      freie.push({ text: f.text, betrag: b, steuer: f.steuer });
    }
    if (!positionen.length && !freie.length) return { body: null, fehler: null };
    return { body: { positionen, frei: freie, grund: grund.trim() || null }, fehler: null };
  }
  const a = anfrage();
  const json = a.body ? JSON.stringify(a.body) : null;

  // Zusammenfassung vom Server, 300 ms nach der letzten Änderung (ältere Antworten zählen nicht). Schreiben geht erst,
  // wenn sie zu den Eingaben passt: eine geschriebene Korrektur lässt sich nicht zurücknehmen.
  useEffect(() => {
    const n = ++lauf.current;
    setFehler(null);
    if (!id || !json || !stand?.moeglich) {
      setPruefung(null);
      return;
    }
    const t = window.setTimeout(() => {
      api
        .post<KorrekturPruefung>(url(id, '/pruefen'), JSON.parse(json))
        .then((p) => n === lauf.current && setPruefung({ json, p }))
        .catch((err) => {
          if (n !== lauf.current) return;
          setPruefung(null);
          setFehler(errorText(err));
        });
    }, 300);
    return () => window.clearTimeout(t);
  }, [id, json, stand?.moeglich]);

  async function zeigen() {
    if (!id || !a.body) return;
    setBusy(true);
    try {
      setVorschau(URL.createObjectURL(await api.blob(url(id, '/vorschau'), a.body)));
    } catch (err) {
      setFehler(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function schreiben() {
    if (!id || !a.body) return;
    setBusy(true);
    setFehler(null);
    try {
      const res = await api.post<KorrekturErgebnis>(url(id), a.body);
      await onDone(res);
    } catch (err) {
      setFehler(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const setWahl = (n: number, w: Partial<PosWahl>) => setPos((x) => ({ ...x, [n]: { betrag: false, wert: '', ...x[n], ...w } }));
  const setFreiZeile = (key: number, f: Partial<Frei>) => setFrei((x) => x.map((r) => (r.key === key ? { ...r, ...f } : r)));
  const neuFrei = () =>
    setFrei((x) => [...x, { key: nextKey++, text: '', betrag: '', steuer: ku ? '0' : (steuernMitRest[0]?.key ?? stand?.steuern[0]?.key ?? '19') }]);

  const kopf = stand
    ? [
        `Rechnung ${eurC(stand.betrag, wa)}`,
        ...(stand.gutgeschrieben > 0 ? [`schon gutgeschrieben ${eurC(stand.gutgeschrieben, wa)}`] : []),
        ...(stand.bezahlt > 0 ? [`bezahlt ${eurC(stand.bezahlt, wa)}`] : []),
      ].join(' · ')
    : '';
  const p = pruefung && pruefung.json === json ? pruefung.p : null;

  return (
    <>
      <Dialog open={!!item} onClose={onClose} title={item ? `Gutschrift / Korrektur zu ${item.number}` : ''} className="rk-dlg">
        {item && !stand && (ladeFehler ? <div className="notice bad">{ladeFehler}</div> : <p className="muted">Wird geladen …</p>)}
        {item && stand && !stand.moeglich && (
          <>
            <div className="notice">{stand.grund}</div>
            <div className="dlg-actions">
              <button type="button" className="btn primary" onClick={onClose}>
                Schließen
              </button>
            </div>
          </>
        )}
        {item && stand?.moeglich && (
          <>
            <p className="rt-hint">
              Schreiben Sie Ihrem Kunden einen Teil der Rechnung gut. Es entsteht eine Rechnungskorrektur mit eigener Nummer. Die Rechnung selbst bleibt,
              wie sie ist.
            </p>
            {lagerSatz && <p className="rt-hint rk-lager">{lagerSatz}</p>}
            <p className="rk-kopf num">{kopf}</p>

            <fieldset className="rk-teil" disabled={alles}>
              <legend>Positionen</legend>
              <ul className="rk-pos">
                {stand.positionen.map((x) => {
                  const w = pos[x.pos] ?? { betrag: false, wert: '' };
                  const ganz = x.nochNetto <= 0;
                  const feld = w.betrag ? (ku ? 'Betrag' : 'Betrag netto') : 'Menge';
                  return (
                    <li key={x.pos} className={ganz ? 'rk-ganz' : ''}>
                      <span className="rk-nr num">{x.pos}</span>
                      <span className="rk-was">
                        {x.description}
                        <small className="muted">
                          {ganz
                            ? 'ganz gutgeschrieben'
                            : `noch: ${[deQty(x.nochMenge), x.unit].filter(Boolean).join(' ')} · ${eurC(x.nochNetto, wa)}${ku ? '' : ' netto'}`}
                        </small>
                      </span>
                      {!ganz && (
                        <span className="rk-ein">
                          <label className="field">
                            <span>{feld}</span>
                            <input
                              value={w.wert}
                              inputMode="decimal"
                              aria-label={`Position ${x.pos}: ${feld}`}
                              onChange={(e) => setWahl(x.pos, { wert: e.target.value })}
                            />
                          </label>
                          <button type="button" className="linkbtn" onClick={() => setWahl(x.pos, { betrag: !w.betrag, wert: '' })}>
                            {w.betrag ? 'Menge statt Betrag' : 'Betrag statt Menge'}
                          </button>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>

              {frei.length > 0 && (
                <ul className="rk-frei">
                  {frei.map((f) => (
                    <li key={f.key}>
                      <label className="field rk-frei-text">
                        <span>Wofür</span>
                        <input value={f.text} maxLength={200} placeholder="z. B. Nachlass wegen Verspätung" onChange={(e) => setFreiZeile(f.key, { text: e.target.value })} />
                      </label>
                      <label className="field rk-frei-betrag">
                        <span>{ku ? 'Betrag' : 'Betrag netto'}</span>
                        <input value={f.betrag} inputMode="decimal" onChange={(e) => setFreiZeile(f.key, { betrag: e.target.value })} />
                      </label>
                      {!ku && steuernMitRest.length > 1 && (
                        <ChipRadios
                          label="Umsatzsteuer"
                          className="rk-frei-steuer"
                          value={f.steuer}
                          options={steuernMitRest.map((s) => [s.key, s.label] as const)}
                          onChange={(v) => setFreiZeile(f.key, { steuer: v })}
                        />
                      )}
                      <button type="button" className="linkbtn rt-danger rk-frei-weg" onClick={() => setFrei((x) => x.filter((r) => r.key !== f.key))}>
                        Entfernen
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {frei.length < MAX_FREI && (
                <button type="button" className="linkbtn rk-frei-neu" onClick={neuFrei}>
                  + Freier Betrag
                </button>
              )}
            </fieldset>

            <label className="check rk-alles">
              <input type="checkbox" checked={alles} onChange={(e) => setAlles(e.target.checked)} />
              <span>Alles Übrige gutschreiben ({eurC(rest, wa)})</span>
            </label>
            <label className="field">
              <span>Grund (steht auf der Korrektur, freiwillig)</span>
              <input value={grund} maxLength={300} onChange={(e) => setGrund(e.target.value)} />
            </label>

            {p && (
              <div className="rk-summe" aria-live="polite">
                <p className="num">
                  {ku
                    ? `Rechnungskorrektur: zusammen ${minus(p.brutto, wa)}`
                    : `Rechnungskorrektur: netto ${minus(p.netto, wa)} · USt ${minus(p.ust, wa)} · zusammen ${minus(p.brutto, wa)}`}
                </p>
                <p className="num">Rechnung neu: {eurC(p.rechnungNeu, wa)}</p>
                {(p.voll || p.offen > 0 || p.erstattung > 0) && (
                  <p>
                    {[
                      ...(p.voll ? ['Die Rechnung ist damit ganz gutgeschrieben.'] : []),
                      ...(p.offen > 0 ? [`Offen bleiben ${eurC(p.offen, wa)}.`] : []),
                      ...(p.erstattung > 0 ? [`Ihr Kunde hat schon mehr bezahlt. Zu erstatten: ${eurC(p.erstattung, wa)}.`] : []),
                    ].join(' ')}
                  </p>
                )}
              </div>
            )}
            {(a.fehler ?? fehler) && <div className="notice bad">{a.fehler ?? fehler}</div>}

            <div className="dlg-actions">
              <button type="button" className="btn" onClick={onClose}>
                Abbrechen
              </button>
              <button type="button" className="btn" disabled={busy || !a.body} onClick={() => void zeigen()}>
                Vorschau
              </button>
              <button type="button" className="btn primary" disabled={busy || !a.body || !p} onClick={() => void schreiben()}>
                Rechnungskorrektur schreiben
              </button>
            </div>
          </>
        )}
      </Dialog>
      <PdfDialog url={vorschau} title="Vorschau" onClose={() => setVorschau(null)} />
    </>
  );
}
